import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import isEmpty from 'lodash/isEmpty';
import type { ReactElement } from 'react';
import { Fragment, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';

import type { AccountApiError } from '@/types/accountApiType';

import { Flex } from '@/components/Flex';
import { LoginRequired } from '@/components/LoginRequired';
import { Typography } from '@/components/Typography';
import { FixedPageDocument } from '@/graphql/contentful/api';
import { useNewsLetter } from '@/hooks/useNewsLetter';
import type { ContentfulFixedPageCMSData } from '@/integrations/contentful';
import {
  generateSectionKey,
  renderContentfulFixedPage,
} from '@/integrations/contentful';
import { Default } from '@/layouts/Default';
import { NewsLetterUnSubSuccessDescUser } from '@/messages/newsletter';

import type { NewsLetterUnsubscribeFormSchema } from './NewsLetterUnsubscribeForm';
import { NewsLetterUnsubscribeForm } from './NewsLetterUnsubscribeForm';

import * as S from './NewsLetterView.styles';
export interface NewsLetterUnsubscribeViewProps {
  cms?: ContentfulFixedPageCMSData;
}
export const NewsLetterUnsubscribeView = ({
  cms,
}: NewsLetterUnsubscribeViewProps) => {
  const liveCms = useContentfulLiveUpdates(cms, {
    query: FixedPageDocument,
  });
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState<AccountApiError | undefined>(undefined);
  const { unSubscribeNewsLetterForUser } = useNewsLetter();
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit: SubmitHandler<NewsLetterUnsubscribeFormSchema> = async ({
    email,
  }: NewsLetterUnsubscribeFormSchema) => {
    try {
      setLoading(true);
      const res = await unSubscribeNewsLetterForUser({
        email,
      });
      setLoading(false);
      if (res.code === 200) {
        setIsSubscribed(true);
        setError(undefined);
      } else {
        setError({ code: res.code, msg: res.msg ?? res.message });
      }
    } catch (error) {
      setLoading(false);
    }
  };
  return (
    <LoginRequired>
      <Flex
        alignItems='center'
        backgroundColor='lightGrey'
        flexDirection='column'
      >
        {liveCms?.topContent?.map((data, i) => (
          <Fragment key={generateSectionKey(data, i)}>
            {renderContentfulFixedPage(
              data,
              undefined,
              liveCms.cmsExtraData,
              false,
              false,
            )}
          </Fragment>
        ))}
        {isSubscribed ? (
          <Typography data-testid='unsubscribed' margin={6} textAlign='center'>
            {NewsLetterUnSubSuccessDescUser}
          </Typography>
        ) : (
          <S.FormWrap $background='#FAFAFA' data-testid='newsletterForm'>
            <NewsLetterUnsubscribeForm
              handleSubmit={handleSubmit}
              isProcessing={loading}
            ></NewsLetterUnsubscribeForm>
            {!isEmpty(error) && error.msg && (
              <Typography color='error' scale='p3' textAlign='center'>
                {error.msg}
              </Typography>
            )}
          </S.FormWrap>
        )}
      </Flex>
    </LoginRequired>
  );
};

NewsLetterUnsubscribeView.getLayout = (page: ReactElement) => {
  return <Default cmsData={page.props.cmsDefaultLayoutData}>{page}</Default>;
};
